import React, { useEffect, useState } from "react";
import { X, Plus, Trash2, Save, Loader2, Download, Pill, FileText, ExternalLink, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import useMemedPrescription from "@/hooks/useMemedPrescription";

const ROUTES = ["VO", "SL", "SC", "IM", "EV", "Inalatória", "Tópica", "Oftálmica", "Otológica", "Retal", "Vaginal"];
const RX_TYPES = [
    { value: "comum", label: "Receita Simples" },
    { value: "controlado", label: "Receita de Controle Especial" },
    { value: "especial", label: "Receituário Especial" },
];

function emptyItem() {
    return { name: "", dose: "", route: "VO", frequency: "", duration: "", cid10: "", notes: "", controlled: false };
}

export default function PrescriptionDialog({
    patient,
    consultationId,
    preselectedMeds = [],
    activeProblems = [],
    onClose,
    onSaved,
}) {
    const [items, setItems] = useState(() => {
        if (preselectedMeds && preselectedMeds.length > 0) {
            return preselectedMeds.map((m) => ({
                ...emptyItem(),
                name: m.name || m.nome || "",
                dose: m.dose || m.dosage || "",
                frequency: m.frequency || m.posologia || "",
            }));
        }
        return [emptyItem()];
    });
    const [orient, setOrient] = useState("");
    const [validityDays, setValidityDays] = useState(30);
    const [rxType, setRxType] = useState("comum");
    const [saving, setSaving] = useState(false);
    const [savedId, setSavedId] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [showMemedInfo, setShowMemedInfo] = useState(false);
    const [showProfileForm, setShowProfileForm] = useState(false);
    const [profile, setProfile] = useState({ cpf: "", birth_date: "", crm: "", crm_uf: "SP", first_name: "", last_name: "" });
    const [savingProfile, setSavingProfile] = useState(false);

    const memed = useMemedPrescription({
        patient,
        consultationId,
        onEmitted: (payload) => {
            toast.success("Receita Memed emitida e registrada no prontuário.");
            onSaved?.(payload || {});
        },
    });

    // Carrega o perfil atual do médico quando o formulário abre
    useEffect(() => {
        if (!showProfileForm) return;
        (async () => {
            try {
                const { data } = await api.get("/auth/me");
                setProfile((p) => ({
                    ...p,
                    cpf: data.cpf || "",
                    birth_date: data.birth_date || "",
                    crm: data.crm || "",
                    crm_uf: data.crm_uf || "SP",
                    first_name: data.first_name || (data.name ? data.name.split(" ")[0] : ""),
                    last_name: data.last_name || (data.name ? data.name.split(" ").slice(1).join(" ") : ""),
                }));
            } catch (_) { /* ignore */ }
        })();
    }, [showProfileForm]);

    const saveProfileAndRetry = async () => {
        setSavingProfile(true);
        try {
            await api.patch("/auth/me", profile);
            toast.success("Perfil atualizado. Abrindo Memed...");
            setShowProfileForm(false);
            setTimeout(() => memed.open(), 300);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingProfile(false);
        }
    };

    // Detecta se o erro é por dados ausentes no perfil
    const needsProfile = memed.error && /Dados obrigatórios ausentes|no perfil do médico|cadastrar o prescritor/i.test(memed.error);

    const setItem = (i, patch) =>
        setItems((arr) => arr.map((it, ix) => (ix === i ? { ...it, ...patch } : it)));
    const addItem = () => setItems((arr) => [...arr, emptyItem()]);
    const removeItem = (i) => setItems((arr) => arr.filter((_, ix) => ix !== i));

    const suggestCid = (item) => {
        // Match medication name to a problem by fuzzy substring; prefill CID
        const nameL = (item.name || "").toLowerCase();
        for (const p of activeProblems) {
            const t = (p.title || "").toLowerCase();
            if (!t || !p.cid10) continue;
            if (nameL && (t.split(" ").some((w) => w.length > 3 && nameL.includes(w)))) return p.cid10;
        }
        return null;
    };

    const submit = async () => {
        const valid = items.filter((i) => (i.name || "").trim());
        if (valid.length === 0) { toast.error("Adicione pelo menos 1 medicamento com nome."); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/prescriptions", {
                patient_id: patient.id,
                consultation_id: consultationId,
                items: valid,
                general_orientations: orient || null,
                validity_days: Number(validityDays) || 30,
                prescription_type: rxType,
            });
            setSavedId(data.id);
            toast.success("Receita emitida. Clique em Baixar PDF.");
            onSaved?.(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const downloadPdf = async () => {
        if (!savedId) return;
        setDownloading(true);
        try {
            const resp = await api.get(`/prescriptions/${savedId}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `receita_${patient.full_name?.replace(/\s+/g, "_") || "paciente"}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDownloading(false); }
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="prescription-dialog"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col border-2 border-[#A08A5C]/40">
                <div className="p-5 border-b-2 border-[#EDE0C6] flex items-center justify-between bg-gradient-to-r from-[#FBF7EF] to-white rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full bg-brand text-white flex items-center justify-center">
                            <Pill size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-widest text-[#8A6A15] font-semibold">
                                Prescrição digital
                            </p>
                            <h3 className="font-display text-xl text-brand font-medium">
                                Nova receita — {patient?.full_name}
                            </h3>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full bg-alt hover:bg-brand hover:text-white flex items-center justify-center transition-colors"
                        data-testid="prescription-close"
                        aria-label="Fechar"
                    >
                        ✕
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 bg-white space-y-4">
                    {/* --- Memed integration slot --- */}
                    <div
                        className="rounded-xl border-2 border-[#2E7D6B] bg-[#EAF6F2] p-4"
                        data-testid="memed-integration-slot"
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#2E7D6B] text-white flex items-center justify-center shrink-0">
                                <ExternalLink size={18} />
                            </div>
                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <h4 className="font-display text-base text-[#1F5A4C] font-semibold">
                                        Prescrever via Memed
                                    </h4>
                                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#2E7D6B] text-white font-bold">
                                        Produção
                                    </span>
                                </div>
                                <p className="text-sm text-[#1F5A4C] leading-snug">
                                    Abre o widget oficial da Memed já com o paciente pré-carregado. A receita é
                                    assinada digitalmente pela Memed e sincronizada automaticamente com o prontuário.
                                </p>
                                {memed.error && (
                                    <div
                                        className="mt-2 rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-800"
                                        data-testid="memed-error"
                                    >
                                        <b>Não foi possível abrir a Memed:</b> {memed.error}
                                        {needsProfile && !showProfileForm && (
                                            <button
                                                type="button"
                                                onClick={() => setShowProfileForm(true)}
                                                className="ml-2 underline font-semibold text-red-900 hover:text-red-700"
                                                data-testid="memed-complete-profile"
                                            >
                                                Completar perfil agora
                                            </button>
                                        )}
                                    </div>
                                )}

                                {showProfileForm && (
                                    <div
                                        className="mt-3 rounded-lg bg-white border-2 border-[#2E7D6B] p-3 space-y-2"
                                        data-testid="memed-profile-form"
                                    >
                                        <p className="text-xs text-[#1F5A4C] font-semibold">
                                            Complete seus dados profissionais (exigência RDC 1000/25 da Memed).
                                            Salvo apenas 1 vez.
                                        </p>
                                        <div className="grid md:grid-cols-2 gap-2">
                                            <input
                                                className="input-strong text-sm"
                                                placeholder="Nome (primeiro)"
                                                value={profile.first_name}
                                                onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                                                data-testid="memed-first-name"
                                            />
                                            <input
                                                className="input-strong text-sm"
                                                placeholder="Sobrenome"
                                                value={profile.last_name}
                                                onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                                                data-testid="memed-last-name"
                                            />
                                            <input
                                                className="input-strong text-sm"
                                                placeholder="CPF (apenas números)"
                                                inputMode="numeric"
                                                maxLength={14}
                                                value={profile.cpf}
                                                onChange={(e) => setProfile({ ...profile, cpf: e.target.value })}
                                                data-testid="memed-cpf"
                                            />
                                            <input
                                                className="input-strong text-sm"
                                                placeholder="Data nasc. (DD/MM/AAAA)"
                                                value={profile.birth_date}
                                                onChange={(e) => setProfile({ ...profile, birth_date: e.target.value })}
                                                data-testid="memed-birthdate"
                                            />
                                            <input
                                                className="input-strong text-sm"
                                                placeholder="CRM (apenas números)"
                                                value={profile.crm}
                                                onChange={(e) => setProfile({ ...profile, crm: e.target.value })}
                                                data-testid="memed-crm"
                                            />
                                            <input
                                                className="input-strong text-sm"
                                                placeholder="UF do CRM (SP)"
                                                maxLength={2}
                                                value={profile.crm_uf}
                                                onChange={(e) => setProfile({ ...profile, crm_uf: e.target.value.toUpperCase() })}
                                                data-testid="memed-crm-uf"
                                            />
                                        </div>
                                        <div className="flex justify-end gap-2 pt-1">
                                            <button
                                                type="button"
                                                onClick={() => setShowProfileForm(false)}
                                                className="btn-ghost text-xs"
                                                data-testid="memed-profile-cancel"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={saveProfileAndRetry}
                                                disabled={savingProfile}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#2E7D6B] text-white text-xs font-medium hover:bg-[#1F5A4C] disabled:opacity-60"
                                                data-testid="memed-profile-save"
                                            >
                                                {savingProfile ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                                Salvar e abrir Memed
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <div className="flex flex-wrap gap-2 mt-3">
                                    <button
                                        type="button"
                                        onClick={memed.open}
                                        disabled={memed.loading}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#2E7D6B] text-white text-sm font-medium hover:bg-[#1F5A4C] disabled:opacity-60 transition-colors"
                                        data-testid="prescription-memed-btn"
                                    >
                                        {memed.loading ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <ExternalLink size={14} />
                                        )}
                                        {memed.loading ? "Abrindo Memed…" : "Prescrever via Memed"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowMemedInfo(true)}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white text-[#1F5A4C] text-sm font-medium hover:bg-[#D8ECE5] transition-colors border border-[#2E7D6B]/40"
                                        data-testid="prescription-memed-info"
                                    >
                                        <Info size={14} />
                                        Sobre a integração
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Rx type + validity */}
                    <div className="grid md:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-sm font-semibold text-brand mb-1.5">Tipo</label>
                            <select
                                value={rxType}
                                onChange={(e) => setRxType(e.target.value)}
                                className="input-strong"
                                data-testid="rx-type"
                            >
                                {RX_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-brand mb-1.5">Validade (dias)</label>
                            <input
                                type="number"
                                min={1}
                                max={180}
                                value={validityDays}
                                onChange={(e) => setValidityDays(e.target.value)}
                                className="input-strong"
                                data-testid="rx-validity"
                            />
                        </div>
                    </div>

                    {/* Items */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="font-display text-base text-brand">Medicamentos</h4>
                            <button onClick={addItem} className="btn-ghost text-xs" data-testid="rx-add-item">
                                <Plus size={12} /> Adicionar
                            </button>
                        </div>
                        <div className="space-y-3">
                            {items.map((item, i) => (
                                <div
                                    key={i}
                                    className="rounded-xl border-2 border-[#A08A5C]/30 p-3 bg-[#FBF7EF]/40"
                                    data-testid={`rx-item-${i}`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs uppercase tracking-widest text-[#8A6A15] font-semibold">
                                            Item #{i + 1}
                                        </span>
                                        {items.length > 1 && (
                                            <button
                                                onClick={() => removeItem(i)}
                                                className="text-red-700 hover:text-red-800"
                                                data-testid={`rx-remove-${i}`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid md:grid-cols-6 gap-2">
                                        <input
                                            className="input-strong md:col-span-3 text-sm"
                                            placeholder="Ex.: Losartana 50mg"
                                            value={item.name}
                                            onChange={(e) => setItem(i, { name: e.target.value })}
                                            data-testid={`rx-name-${i}`}
                                        />
                                        <input
                                            className="input-strong md:col-span-1 text-sm"
                                            placeholder="Dose (1cp)"
                                            value={item.dose}
                                            onChange={(e) => setItem(i, { dose: e.target.value })}
                                            data-testid={`rx-dose-${i}`}
                                        />
                                        <select
                                            className="input-strong md:col-span-1 text-sm"
                                            value={item.route}
                                            onChange={(e) => setItem(i, { route: e.target.value })}
                                            data-testid={`rx-route-${i}`}
                                        >
                                            {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
                                        </select>
                                        <input
                                            className="input-strong md:col-span-1 text-sm font-mono"
                                            placeholder="CID-10"
                                            value={item.cid10}
                                            onChange={(e) => setItem(i, { cid10: e.target.value })}
                                            data-testid={`rx-cid-${i}`}
                                        />
                                    </div>
                                    <div className="grid md:grid-cols-6 gap-2 mt-2">
                                        <input
                                            className="input-strong md:col-span-3 text-sm"
                                            placeholder="Frequência (ex.: 1x/dia pela manhã)"
                                            value={item.frequency}
                                            onChange={(e) => setItem(i, { frequency: e.target.value })}
                                            data-testid={`rx-freq-${i}`}
                                        />
                                        <input
                                            className="input-strong md:col-span-2 text-sm"
                                            placeholder="Duração (30 dias)"
                                            value={item.duration}
                                            onChange={(e) => setItem(i, { duration: e.target.value })}
                                            data-testid={`rx-duration-${i}`}
                                        />
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={!!item.controlled}
                                                onChange={(e) => setItem(i, { controlled: e.target.checked })}
                                                className="w-4 h-4"
                                                data-testid={`rx-controlled-${i}`}
                                            />
                                            Controlado
                                        </label>
                                    </div>
                                    <input
                                        className="input-strong text-sm mt-2"
                                        placeholder="Observações específicas (ex.: tomar com alimento)"
                                        value={item.notes}
                                        onChange={(e) => setItem(i, { notes: e.target.value })}
                                        data-testid={`rx-notes-${i}`}
                                    />
                                    {!item.cid10 && suggestCid(item) && (
                                        <button
                                            onClick={() => setItem(i, { cid10: suggestCid(item) })}
                                            className="text-xs text-brand hover:underline mt-1"
                                            data-testid={`rx-suggest-cid-${i}`}
                                        >
                                            Sugerir CID-10: {suggestCid(item)} (do Problem List)
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-brand mb-1.5">Orientações gerais</label>
                        <textarea
                            rows={3}
                            value={orient}
                            onChange={(e) => setOrient(e.target.value)}
                            className="input-strong"
                            placeholder="Ex.: Manter dieta hipossódica, retornar em 30 dias com exames."
                            data-testid="rx-orientations"
                        />
                    </div>
                </div>

                <div className="p-4 border-t-2 border-[#EDE0C6] flex justify-between items-center gap-2 bg-white rounded-b-2xl flex-wrap">
                    <div className="text-xs text-muted-clinical">
                        {savedId ? <span className="text-brand font-medium">Receita salva no prontuário — pronta para download.</span>
                                 : "Ao salvar, a receita fica registrada no prontuário do paciente."}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="btn-ghost text-sm">Fechar</button>
                        {savedId ? (
                            <button
                                onClick={downloadPdf}
                                disabled={downloading}
                                className="btn-primary"
                                data-testid="rx-download-pdf"
                            >
                                {downloading ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
                                Baixar PDF
                            </button>
                        ) : (
                            <button
                                onClick={submit}
                                disabled={saving}
                                className="btn-primary"
                                data-testid="rx-save"
                            >
                                {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                                Emitir receita
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showMemedInfo && (
                <div
                    className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setShowMemedInfo(false); }}
                    data-testid="memed-info-modal"
                >
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 border-2 border-[#2E7D6B]/40">
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-full bg-[#2E7D6B] text-white flex items-center justify-center">
                                    <ExternalLink size={16} />
                                </div>
                                <h3 className="font-display text-lg text-[#1F5A4C] font-semibold">
                                    Integração Memed — Status
                                </h3>
                            </div>
                            <button
                                onClick={() => setShowMemedInfo(false)}
                                className="w-8 h-8 rounded-full bg-alt hover:bg-brand hover:text-white flex items-center justify-center"
                                aria-label="Fechar"
                                data-testid="memed-info-close"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="space-y-3 text-sm text-brand leading-relaxed">
                            <p>
                                A integração com a <b>Memed</b> está ativa em <b>produção</b>. Ao clicar em
                                &quot;Prescrever via Memed&quot;:
                            </p>
                            <ul className="list-disc pl-5 space-y-1.5 text-[13px]">
                                <li>O widget oficial da Memed é aberto em modal seguro.</li>
                                <li>O paciente atual é pré-carregado (nome, CPF, telefone, endereço).</li>
                                <li>A receita é assinada digitalmente pela Memed (ICP-Brasil quando aplicável).</li>
                                <li>Após emitida, é sincronizada automaticamente com o prontuário e aparece na Linha do Tempo.</li>
                                <li>Se preferir, use a receita interna em PDF pelo botão &quot;Emitir receita&quot; abaixo.</li>
                            </ul>
                            <div className="bg-[#EAF6F2] border border-[#2E7D6B]/40 rounded-lg p-3 space-y-2">
                                <p className="text-xs font-semibold text-[#1F5A4C]">
                                    Token de assinatura por SMS não chega ou é rejeitado?
                                </p>
                                <p className="text-[11px] text-[#1F5A4C]">
                                    A Memed envia um código por SMS para o telefone cadastrado no prescritor.
                                    Se o telefone estava ausente/errado quando o cadastro foi criado,
                                    ressincronize com os dados atuais do seu perfil.
                                </p>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            const { data } = await api.post("/memed/sync-doctor");
                                            toast.success(`Cadastro Memed atualizado (${data.message}). Peça novo SMS.`);
                                        } catch (e) {
                                            toast.error(formatApiError(e));
                                        }
                                    }}
                                    className="text-xs px-3 py-1.5 rounded-lg bg-[#2E7D6B] text-white hover:bg-[#1F5A4C]"
                                    data-testid="memed-sync-doctor"
                                >
                                    Ressincronizar meu telefone com a Memed
                                </button>
                            </div>
                            <p className="text-xs text-muted-clinical bg-[#FBF7EF] border border-[#EDE0C6] rounded-lg p-2.5">
                                <b>Importante:</b> o prescritor precisa estar cadastrado e aprovado no painel da Memed
                                para que o widget abra. Caso apareça &quot;médico não encontrado&quot;, verifique o cadastro
                                em <a href="https://prescricao.memed.com.br" target="_blank" rel="noopener" className="underline">prescricao.memed.com.br</a>.
                            </p>
                        </div>
                        <div className="flex justify-end mt-4">
                            <button
                                onClick={() => setShowMemedInfo(false)}
                                className="btn-primary text-sm"
                                data-testid="memed-info-ok"
                            >
                                Entendi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
